import React, { useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import PerfectScrollbar from 'react-perfect-scrollbar';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from '@material-ui/core';

import Datetime from 'components/DataTypes/Datetime';
import { StatusBullet } from 'components';
import AnalyticsDetailsDialog from './AnalyticsDetailsDialog';

const useStyles = makeStyles(theme => ({
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 1050,
  },
  actions: {
    justifyContent: 'flex-end',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  numberElement: {
    textAlign: 'right',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  statusBullet: {
    marginRight: theme.spacing(1),
  },
  notAvailable: {
    color: 'rgba(0,0,0,0.4)',
    fontStyle: 'italic',
  },
  clickableRow: {
    cursor: 'pointer',
  },
  // Two-line cell: a primary line and a muted secondary line stacked vertically.
  stack: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.3,
  },
  secondaryLine: {
    color: 'rgba(0,0,0,0.54)',
    fontSize: '0.8125rem',
  },
}));

const successColor = success => {
  if (success === true) return 'success';
  if (success === false) return 'danger';
  return 'neutral';
};

// Name over email, each preferring the account value and falling back to the profile's.
// Names appear once the read model exposes them; until then the email line stands alone.
const identityCell = (item, classes, na) => {
  const name = (item.accountName || item.profileName || '').trim();
  const email = (item.accountEmail || item.profileEmail || '').trim();
  if (!name && !email) return na;
  return (
    <div className={classes.stack}>
      {name && <span>{name}</span>}
      {email && <span className={name ? classes.secondaryLine : undefined}>{email}</span>}
    </div>
  );
};

const AnalyticsTable = props => {
  const {
    className, loading, items, total, page, rowsPerPage, onChangePage, ...rest
  } = props;
  const classes = useStyles();
  const [selected, setSelected] = useState(null);

  const handlePageChange = (event, newPage) => onChangePage(newPage);

  const na = <span className={classes.notAvailable}>—</span>;

  return (
    <Card {...rest} className={clsx(className)}>
      <CardContent className={classes.content}>
        <PerfectScrollbar>
          <div className={classes.inner}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.nowrap}>Generated at</TableCell>
                  <TableCell className={classes.nowrap}>Type</TableCell>
                  <TableCell className={classes.nowrap}>Key</TableCell>
                  <TableCell className={classes.nowrap}>Name</TableCell>
                  <TableCell className={classes.nowrap}>Application</TableCell>
                  <TableCell className={classes.nowrap}>Result</TableCell>
                  <TableCell className={classes.numberElement}>Elapsed (ms)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={loading ? 'loading' : ''}>
                {items.map(item => (
                  <TableRow
                    hover
                    key={item.id}
                    className={classes.clickableRow}
                    onClick={() => setSelected(item)}
                  >
                    <TableCell className={classes.nowrap}>
                      <Datetime value={item.generatedAt} />
                    </TableCell>
                    <TableCell>{item.type || na}</TableCell>
                    <TableCell>{item.key || na}</TableCell>
                    <TableCell>{identityCell(item, classes, na)}</TableCell>
                    <TableCell className={classes.nowrap}>
                      {item.applicationName ? (
                        <div className={classes.stack}>
                          <span>{item.applicationName}</span>
                          {item.applicationVersion && (
                            <span className={classes.secondaryLine}>{item.applicationVersion}</span>
                          )}
                        </div>
                      ) : na}
                    </TableCell>
                    <TableCell>
                      {item.success === null || item.success === undefined ? na : (
                        <div className={classes.statusContainer}>
                          <StatusBullet
                            className={classes.statusBullet}
                            color={successColor(item.success)}
                            size="sm"
                          />
                          {item.success ? 'Success' : 'Failure'}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={classes.numberElement}>
                      {item.elapsedMs === null || item.elapsedMs === undefined ? na : item.elapsedMs}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body1" align="center">No analytics events match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </CardContent>
      <CardActions className={classes.actions}>
        <TablePagination
          component="div"
          count={total}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={() => {}}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          showFirstButton
          showLastButton
        />
      </CardActions>
      <AnalyticsDetailsDialog
        item={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
      />
    </Card>
  );
};

AnalyticsTable.propTypes = {
  className: PropTypes.string,
  items: PropTypes.array.isRequired,
  total: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  loading: PropTypes.bool,
  onChangePage: PropTypes.func.isRequired,
};

export default AnalyticsTable;